import { useEffect, useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, FormGroup, Label, Input, Spinner } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { saveAsset, saveTask, completeTask, uploadTaskFiles } from './Api';

const todayIso = () => new Date().toISOString().slice(0, 10);
const isoDay = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');
const fmtKm = (n) => (n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString('en-US'));

const REPEAT_MONTHS = [
  { value: '', label: 'Does not repeat by date' },
  { value: '1', label: 'Every month' },
  { value: '3', label: 'Every 3 months' },
  { value: '6', label: 'Every 6 months' },
  { value: '12', label: 'Every year' },
  { value: '24', label: 'Every 2 years' },
  { value: '36', label: 'Every 3 years' },
];

const Required = () => <span className="st-required" aria-hidden="true"> *</span>;

const Footer = ({ onClose, onSave, saving, label }) => (
  <ModalFooter>
    <button type="button" className="it-btn it-btn--cancel" onClick={onClose} disabled={saving}>Cancel</button>
    <button type="button" className="it-btn it-btn--save" onClick={onSave} disabled={saving} aria-busy={saving}>
      {saving ? <Spinner size="sm" /> : <FeatherIcon icon="check" size={13} />} {label}
    </button>
  </ModalFooter>
);

// ── A vehicle or a piece of equipment ────────────────────────────────────────
export const AssetModal = ({ isOpen, onClose, kind, asset, properties = [], defaults = {}, typeSuggestions = [], onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isVehicle = kind === 'vehicle';

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setForm(
      isVehicle
        ? {
          plateNumber: asset?.vehicle?.plateNumber || '',
          vehicleType: asset?.vehicle?.vehicleType || '',
          model: asset?.vehicle?.model || '',
          currentKm: asset?.vehicle?.currentKm ?? '',
          notes: asset?.notes || '',
        }
        : {
          propertyId: asset?.equipment?.propertyId || defaults.propertyId || '',
          buildingName: asset?.equipment?.buildingName || defaults.buildingName || '',
          equipmentType: asset?.equipment?.equipmentType || '',
          name: asset?.equipment?.name || '',
          notes: asset?.notes || '',
        }
    );
  }, [isOpen, asset, isVehicle, defaults.propertyId, defaults.buildingName]);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  const buildings = useMemo(
    () => (properties.find((p) => p._id === form.propertyId)?.building || []).filter((b) => b?.buildingName),
    [properties, form.propertyId]
  );

  const save = async () => {
    const missing = isVehicle
      ? !String(form.plateNumber || '').trim() && 'Enter the plate number.'
      : (!form.propertyId && 'Choose a property.') || (!String(form.buildingName || '').trim() && 'Choose a building.') || (!String(form.equipmentType || '').trim() && 'Enter the type of equipment.');
    if (missing) {
      setError(missing);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = isVehicle
        ? { kind, notes: form.notes, vehicle: { plateNumber: form.plateNumber, vehicleType: form.vehicleType, model: form.model, currentKm: form.currentKm === '' ? null : Number(form.currentKm) } }
        : { kind, notes: form.notes, equipment: { propertyId: form.propertyId, buildingName: form.buildingName, equipmentType: form.equipmentType, name: form.name } };
      const out = await saveAsset(asset?._id, payload);
      toast.success(out?.message || 'Saved');
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const typeListId = `st-types-${kind}`;
  return (
    <Modal isOpen={isOpen} toggle={saving ? undefined : onClose} centered className="st-modal" backdrop="static">
      <ModalHeader toggle={saving ? undefined : onClose}>{asset ? 'Edit' : 'Add'} {isVehicle ? 'vehicle' : 'equipment'}</ModalHeader>
      <ModalBody>
        <datalist id={typeListId}>{typeSuggestions.map((t) => <option key={t} value={t} />)}</datalist>
        {isVehicle ? (
          <Row className="g-3">
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label for="st-plate">Plate number<Required /></Label>
                <Input id="st-plate" value={form.plateNumber || ''} onChange={set('plateNumber')} maxLength={20} autoComplete="off" style={{ textTransform: 'uppercase' }} />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label for="st-vtype">Type</Label>
                <Input id="st-vtype" list={typeListId} value={form.vehicleType || ''} onChange={set('vehicleType')} placeholder="Truck, van, trailer…" maxLength={80} />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label for="st-model">Model</Label>
                <Input id="st-model" value={form.model || ''} onChange={set('model')} placeholder="Ford F-150 2021" maxLength={120} />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label for="st-km">Current km</Label>
                <Input id="st-km" type="number" inputMode="numeric" min="0" value={form.currentKm ?? ''} onChange={set('currentKm')} />
              </FormGroup>
            </Col>
          </Row>
        ) : (
          <Row className="g-3">
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label for="st-prop">Property<Required /></Label>
                <Input id="st-prop" type="select" value={form.propertyId || ''} onChange={(e) => setForm((prev) => ({ ...prev, propertyId: e.target.value, buildingName: '' }))} disabled={!!asset}>
                  <option value="">Select property</option>
                  {properties.map((p) => <option key={p._id} value={p._id}>{p.title || p.name}</option>)}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label for="st-bldg">Building<Required /></Label>
                {buildings.length ? (
                  <Input id="st-bldg" type="select" value={form.buildingName || ''} onChange={set('buildingName')}>
                    <option value="">Select building</option>
                    {buildings.map((b) => <option key={b.buildingName} value={b.buildingName}>{b.buildingName}</option>)}
                  </Input>
                ) : (
                  <Input id="st-bldg" value={form.buildingName || ''} onChange={set('buildingName')} maxLength={200} />
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label for="st-etype">Type of equipment<Required /></Label>
                <Input id="st-etype" list={typeListId} value={form.equipmentType || ''} onChange={set('equipmentType')} placeholder="Boiler, generator, elevator…" maxLength={120} />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label for="st-ename">Name or tag</Label>
                <Input id="st-ename" value={form.name || ''} onChange={set('name')} placeholder="Boiler 2, roof unit…" maxLength={120} />
              </FormGroup>
            </Col>
          </Row>
        )}
        <FormGroup className="mt-3 mb-0">
          <Label for="st-anotes">Notes</Label>
          <Input id="st-anotes" type="textarea" rows={2} value={form.notes || ''} onChange={set('notes')} maxLength={2000} />
        </FormGroup>
        {error && <div role="alert" className="text-danger mt-3" style={{ fontSize: '0.82rem' }}>{error}</div>}
      </ModalBody>
      <Footer onClose={onClose} onSave={save} saving={saving} label={asset ? 'Save changes' : isVehicle ? 'Add vehicle' : 'Add equipment'} />
    </Modal>
  );
};

// ── A service the asset needs ───────────────────────────────────────────────
export const TaskModal = ({ isOpen, onClose, kind, asset, task, titleSuggestions = [], onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isVehicle = kind === 'vehicle';

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setForm({
      title: task?.title || '',
      dueDate: isoDay(task?.dueDate),
      dueKm: task?.dueKm ?? '',
      repeatMonths: task?.repeat?.months ? String(task.repeat.months) : '',
      repeatKm: task?.repeat?.km ?? '',
      notes: task?.notes || '',
    });
  }, [isOpen, task]);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  const monthOptions = useMemo(
    () => (form.repeatMonths && !REPEAT_MONTHS.some((o) => o.value === form.repeatMonths) ? [...REPEAT_MONTHS, { value: form.repeatMonths, label: `Every ${form.repeatMonths} months` }] : REPEAT_MONTHS),
    [form.repeatMonths]
  );

  const save = async () => {
    if (!String(form.title || '').trim()) {
      setError('Say what service is needed.');
      return;
    }
    if (!form.dueDate && (!isVehicle || form.dueKm === '')) {
      setError(isVehicle ? 'Give a due date, a due km, or both.' : 'Give the date the service is needed.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const out = await saveTask(asset._id, task?._id, {
        title: form.title,
        dueDate: form.dueDate || null,
        dueKm: isVehicle && form.dueKm !== '' ? Number(form.dueKm) : null,
        repeat: {
          months: form.repeatMonths ? Number(form.repeatMonths) : null,
          km: isVehicle && form.repeatKm !== '' ? Number(form.repeatKm) : null,
        },
        notes: form.notes,
      });
      toast.success(out?.message || 'Saved');
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const label = isVehicle ? asset?.vehicle?.plateNumber : [asset?.equipment?.equipmentType, asset?.equipment?.name].filter(Boolean).join(' · ');
  return (
    <Modal isOpen={isOpen} toggle={saving ? undefined : onClose} centered className="st-modal" backdrop="static">
      <ModalHeader toggle={saving ? undefined : onClose}>{task ? 'Edit service' : 'Add service'}{label ? ` · ${label}` : ''}</ModalHeader>
      <ModalBody>
        <datalist id="st-titles">{titleSuggestions.map((t) => <option key={t} value={t} />)}</datalist>
        <FormGroup>
          <Label for="st-title">Service needed<Required /></Label>
          <Input id="st-title" list="st-titles" value={form.title || ''} onChange={set('title')} placeholder={isVehicle ? 'Oil change, tire rotation, inspection…' : 'Annual service, filter change…'} maxLength={200} />
        </FormGroup>
        <Row className="g-3">
          <Col md={isVehicle ? 6 : 12}>
            <FormGroup className="mb-0">
              <Label for="st-due">Needed by{!isVehicle && <Required />}</Label>
              <Input id="st-due" type="date" value={form.dueDate || ''} onChange={set('dueDate')} />
            </FormGroup>
          </Col>
          {isVehicle && (
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label for="st-duekm">Or at km</Label>
                <Input id="st-duekm" type="number" inputMode="numeric" min="0" value={form.dueKm ?? ''} onChange={set('dueKm')} placeholder={asset?.vehicle?.currentKm != null ? `Now ${fmtKm(asset.vehicle.currentKm)}` : ''} />
              </FormGroup>
            </Col>
          )}
          <Col md={isVehicle ? 6 : 12}>
            <FormGroup className="mb-0">
              <Label for="st-rmonths">Repeat</Label>
              <Input id="st-rmonths" type="select" value={form.repeatMonths || ''} onChange={set('repeatMonths')}>
                {monthOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Input>
            </FormGroup>
          </Col>
          {isVehicle && (
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label for="st-rkm">Repeat every (km)</Label>
                <Input id="st-rkm" type="number" inputMode="numeric" min="1" value={form.repeatKm ?? ''} onChange={set('repeatKm')} placeholder="e.g. 8000" />
              </FormGroup>
            </Col>
          )}
        </Row>
        <div className="st-help">
          {isVehicle
            ? 'It is due as soon as the date or the km is reached, whichever comes first. A repeating service schedules the next one when this one is completed.'
            : 'A repeating service schedules the next one when this one is completed.'}
        </div>
        <FormGroup className="mt-3 mb-0">
          <Label for="st-tnotes">Notes</Label>
          <Input id="st-tnotes" type="textarea" rows={2} value={form.notes || ''} onChange={set('notes')} maxLength={2000} />
        </FormGroup>
        {error && <div role="alert" className="text-danger mt-3" style={{ fontSize: '0.82rem' }}>{error}</div>}
      </ModalBody>
      <Footer onClose={onClose} onSave={save} saving={saving} label={task ? 'Save changes' : 'Add service'} />
    </Modal>
  );
};

// ── Recording that a service was done ───────────────────────────────────────
export const CompleteModal = ({ isOpen, onClose, kind, asset, task, onSaved }) => {
  const [date, setDate] = useState(todayIso());
  const [km, setKm] = useState('');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isVehicle = kind === 'vehicle';

  useEffect(() => {
    if (!isOpen) return;
    setDate(todayIso());
    setKm('');
    setNotes('');
    setFiles([]);
    setError('');
  }, [isOpen, task]);

  const save = async () => {
    if (!date || date > todayIso()) {
      setError('Enter a completion date that is not in the future.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const keys = files.length ? await uploadTaskFiles(task._id, files) : [];
      const payload = { completedDate: date, km: isVehicle && km !== '' ? Number(km) : null, notes, files: keys };
      let out;
      try {
        out = await completeTask(task._id, payload);
      } catch (e) {
        if (!e.lowerKm) throw e;
        const answer = await Swal.fire({ title: 'Lower km than recorded', text: e.message, icon: 'question', showCancelButton: true, confirmButtonColor: '#1A6B4A', confirmButtonText: 'Save anyway', cancelButtonText: 'Back', reverseButtons: true });
        if (!answer.isConfirmed) return;
        out = await completeTask(task._id, { ...payload, allowLowerKm: true });
      }
      toast.success(out?.message || 'Completed');
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={saving ? undefined : onClose} centered className="st-modal" backdrop="static">
      <ModalHeader toggle={saving ? undefined : onClose}>Complete: {task?.title}</ModalHeader>
      <ModalBody>
        <Row className="g-3">
          <Col md={isVehicle ? 6 : 12}>
            <FormGroup className="mb-0">
              <Label for="st-cdate">Completion date<Required /></Label>
              <Input id="st-cdate" type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} disabled={saving} />
            </FormGroup>
          </Col>
          {isVehicle && (
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label for="st-ckm">Km at service</Label>
                <Input id="st-ckm" type="number" inputMode="numeric" min="0" value={km} onChange={(e) => setKm(e.target.value)} placeholder={asset?.vehicle?.currentKm != null ? `Now ${fmtKm(asset.vehicle.currentKm)}` : ''} disabled={saving} />
              </FormGroup>
            </Col>
          )}
        </Row>
        <FormGroup className="mt-3">
          <Label for="st-cnotes">Notes</Label>
          <Input id="st-cnotes" type="textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What was done, anything to flag…" maxLength={2000} disabled={saving} />
        </FormGroup>
        <FormGroup className="mb-0">
          <Label for="st-cfiles">Invoice or photos</Label>
          <Input id="st-cfiles" type="file" multiple accept="application/pdf,image/*" onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 10))} disabled={saving} />
          <div className="st-help">{files.length ? `${files.length} file${files.length === 1 ? '' : 's'} selected` : 'PDF or image. An invoice can also be added later from the history.'}</div>
        </FormGroup>
        {task?.repeatLabel && <div className="st-help mt-2"><FeatherIcon icon="repeat" size={11} /> {task.repeatLabel}: the next one is scheduled when you save.</div>}
        {error && <div role="alert" className="text-danger mt-3" style={{ fontSize: '0.82rem' }}>{error}</div>}
      </ModalBody>
      <Footer onClose={onClose} onSave={save} saving={saving} label="Save completion" />
    </Modal>
  );
};
